import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, ExternalLink } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";

const POSITION_LABELS = {
  home:    "Startseite",
  search:  "Musik / Suche",
  release: "Release-Seite",
  artist:  "Artist-Seite",
};

const EMPTY = {
  title: "", image_url: "", link_url: "", alt_text: "",
  positions: [], is_active: true,
};

function SpotForm({ spot, onSave, onCancel }) {
  const [form, setForm] = useState(spot ? { ...spot } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const cls = "w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600";

  const togglePos = (pos) => {
    setForm(f => ({
      ...f,
      positions: f.positions.includes(pos)
        ? f.positions.filter(p => p !== pos)
        : [...f.positions, pos],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.image_url || !form.link_url) {
      setError("Bild-URL und Ziel-Link sind Pflichtfelder.");
      return;
    }
    if (!form.positions.length) {
      setError("Mindestens eine Position auswählen.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (spot?.id) await api.put(`/partner/${spot.id}`, form);
      else await api.post("/partner/", form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-zinc-900/80 border border-white/8 rounded-xl p-5">
      {error && <div className="text-red-400 text-sm bg-red-900/20 px-3 py-2 rounded-md">{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Interner Name</label>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            className={cls} placeholder="z.B. Amazon Music Q2" />
        </div>
        <div>
          <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Alt-Text (optional)</label>
          <input value={form.alt_text} onChange={e => setForm(f => ({ ...f, alt_text: e.target.value }))}
            className={cls} placeholder="z.B. Jetzt bei Amazon Music streamen" />
        </div>
      </div>

      <div>
        <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Bild-URL *</label>
        <input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
          className={cls} placeholder="https://… oder /medien/banner.jpg" required />
        {form.image_url && (
          <div className="mt-2 rounded-lg overflow-hidden max-h-24">
            <img src={form.image_url} alt="Vorschau" className="w-full h-full object-contain max-h-24 object-left"
              onError={e => { e.currentTarget.style.display = "none"; }} />
          </div>
        )}
      </div>

      <div>
        <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Ziel-Link (Affiliate-URL) *</label>
        <input value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))}
          className={cls} placeholder="https://music.amazon.de/…?tag=…" required />
      </div>

      {/* Position checkboxes */}
      <div>
        <label className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-2">Anzeigen auf *</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(POSITION_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => togglePos(key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all text-left ${
                form.positions.includes(key)
                  ? "bg-rose-600/20 border-rose-500/50 text-rose-300"
                  : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={form.is_active}
            onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
            className="w-4 h-4 rounded accent-rose-600" />
          <span className="text-zinc-300 text-sm">Aktiv (sofort sichtbar)</span>
        </label>
        <div className="flex gap-3">
          <button type="button" onClick={onCancel}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-md transition-colors">
            Abbrechen
          </button>
          <button type="submit" disabled={saving}
            className="px-5 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold text-sm rounded-md transition-colors">
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>
    </form>
  );
}

export default function PartnerPage() {
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await api.get("/partner/");
      setSpots(r.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const toggleActive = async (spot) => {
    try {
      await api.put(`/partner/${spot.id}`, { ...spot, is_active: !spot.is_active });
      fetch();
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Werbeplatz wirklich löschen?")) return;
    try { await api.delete(`/partner/${id}`); fetch(); } catch {}
  };

  return (
    <AdminLayout>
      <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-white font-heading">Werbeplätze</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Partner-Banner mit Affiliate-Links verwalten</p>
        </div>
        <button onClick={() => { setCreating(true); setEditing(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-md transition-colors">
          <Plus size={16} /> Neuer Werbeplatz
        </button>
      </div>

      {/* Hinweis */}
      <div className="mb-6 bg-zinc-900/60 border border-white/5 rounded-xl p-4 text-sm text-zinc-400 space-y-1">
        <p className="text-zinc-300 font-semibold text-xs uppercase tracking-widest mb-2">So funktioniert es</p>
        <p>Bilder hochladen über <strong className="text-white">Mediathek</strong> → URL hier einfügen.</p>
        <p>Bilder werden von deiner Domain ausgeliefert – dadurch adblocker-resistent.</p>
        <p>Pro Position wird zufällig ein aktiver Spot ausgewählt wenn mehrere vorhanden.</p>
      </div>

      {creating && (
        <div className="mb-6">
          <SpotForm onSave={() => { setCreating(false); fetch(); }} onCancel={() => setCreating(false)} />
        </div>
      )}
      {editing && (
        <div className="mb-6">
          <SpotForm spot={editing} onSave={() => { setEditing(null); fetch(); }} onCancel={() => setEditing(null)} />
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm text-center py-12">Laden…</div>
      ) : spots.length === 0 ? (
        <div className="text-center py-16 text-zinc-600">
          <p className="text-lg font-semibold mb-1">Noch keine Werbeplätze</p>
          <p className="text-sm">Erstelle den ersten mit „Neuer Werbeplatz".</p>
        </div>
      ) : (
        <div className="space-y-3">
          {spots.map(spot => (
            <div key={spot.id}
              className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                spot.is_active
                  ? "bg-zinc-900 border-white/8"
                  : "bg-zinc-950 border-white/4 opacity-60"
              }`}>
              {/* Thumbnail */}
              <div className="w-20 h-12 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                {spot.image_url ? (
                  <img src={spot.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs">kein Bild</div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{spot.title || "Ohne Titel"}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(spot.positions || []).map(pos => (
                    <span key={pos} className="text-xs px-2 py-0.5 rounded-full bg-rose-900/30 text-rose-400 border border-rose-800/30">
                      {POSITION_LABELS[pos] || pos}
                    </span>
                  ))}
                </div>
                <a href={spot.link_url} target="_blank" rel="noopener noreferrer"
                  className="text-zinc-600 hover:text-zinc-400 text-xs flex items-center gap-1 mt-1 truncate max-w-xs transition-colors">
                  <ExternalLink size={10} /> {spot.link_url}
                </a>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => toggleActive(spot)} title={spot.is_active ? "Deaktivieren" : "Aktivieren"}
                  className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded transition-colors">
                  {spot.is_active
                    ? <ToggleRight size={18} className="text-rose-400" />
                    : <ToggleLeft size={18} />}
                </button>
                <button onClick={() => { setEditing(spot); setCreating(false); }}
                  className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded transition-colors">
                  <Edit size={15} />
                </button>
                <button onClick={() => handleDelete(spot.id)}
                  className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
